export const embedID = 'associate-connect'

export type HashObject = {
  brokers?: string
  listings?: string
}

type MessageBase = {
  id: typeof embedID
}

type UpdateListingHashMessage = {
  hash?: string
  replace?: boolean
  type: 'update-listing-hash'
}

export type AssociateConnectMessage = MessageBase & UpdateListingHashMessage

function isAllowedOrigin(origin: string): boolean {
  return (
    origin.endsWith('.resimplifi.com') ||
    origin === 'https://resimplifi.com' ||
    origin.endsWith('-resimplifi.vercel.app') ||
    origin.endsWith('localhost:9999')
  )
}

export function isAssociateMessage(
  event: MessageEvent<unknown>
): event is MessageEvent<AssociateConnectMessage> {
  const { data, origin } = event

  return (
    isAllowedOrigin(origin) &&
    data !== null &&
    typeof data === 'object' &&
    'id' in data &&
    data.id === embedID &&
    'type' in data &&
    typeof data.type === 'string'
  )
}

export function sendEmbedMessage(message: Omit<AssociateConnectMessage, 'id'>) {
  parent.postMessage(
    {
      ...message,
      id: embedID,
    },
    '*'
  )
}

function cleanHash(hash: string): string {
  return hash.replace(/^[#/]+|^#\//u, '').replace(/\/$/u, '')
}

function hashToParts(hash: string): string[] {
  return hash.split('/')
}

function isEvenArray(parts: string[]): boolean {
  return parts.length % 2 === 0 && parts.every((value) => value.length > 0)
}

function arrayToObject(array: string[]): Record<string, string> {
  if (!isEvenArray(array)) {
    throw new Error('array must have an even number of elements')
  }

  const object: Record<string, string> = {}

  for (let i = 0; i < array.length; i += 2) {
    const key = array[i]
    const value = array[i + 1]

    object[key] = value
  }

  return object
}

function objectToHashObject(object: Record<string, string>): HashObject {
  return {
    listings: 'listings' in object ? object.listings : undefined,
    brokers: 'brokers' in object ? object.brokers : undefined,
  }
}

export function objectToHash(
  object: Record<string, string | undefined>
): string {
  const string = Object.entries(object)
    .flatMap(([key, value]) => (value === undefined ? undefined : [key, value]))
    .filter((value) => value !== undefined)
    .join('/')

  return `/${string}`
}

export function getHashObject(rawHash: string): HashObject | undefined {
  if (rawHash.length === 0) return undefined

  const cleaned = cleanHash(rawHash)

  if (cleaned.length === 0) return undefined

  try {
    return objectToHashObject(arrayToObject(hashToParts(cleaned)))
  } catch {
    return undefined
  }
}
